'use client';

import { c as _c } from "react/compiler-runtime";
import { CONNECTABLE_PLATFORMS } from '@shared/all/crosspost';
import type { CrossPostPlatform } from '@shared/all/validators';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { client } from '../../utils/trpc';
import AutoCrossPostBoardCardsToggle from './AutoCrossPostBoardCardsToggle';
import FacebookCard from './FacebookCard';
import InstagramCard from './InstagramCard';
import { IntegrationSection } from './IntegrationList';
import LinkedinCard from './LinkedinCard';
import OneDriveServerCard from './OneDriveServerCard';
import RunnersList from './RunnersList';
import TiktokCard from './TiktokCard';
import TrelloCard from './TrelloCard';
import TwitterCard from './TwitterCard';
import YoutubeCard from './YoutubeCard';
const ConnectedApps = () => {
  const $ = _c(56);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: ["autoCrossPostSettings"],
      queryFn: _temp,
      retry: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: autoCrossPost
  } = useQuery(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["platformHealth"],
      queryFn: _temp2,
      retry: false
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: health
  } = useQuery(t1);
  let t2;
  if ($[2] !== health?.platforms) {
    t2 = platform => {
      const env = health?.platforms?.[platform];
      return env === "sandbox" || env === "broken";
    };
    $[2] = health?.platforms;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasIssue = t2;
  let t3;
  if ($[4] !== hasIssue) {
    t3 = CONNECTABLE_PLATFORMS.some(hasIssue);
    $[4] = hasIssue;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const anyIssue = t3;
  let t4;
  if ($[6] !== autoCrossPost?.platforms) {
    t4 = autoCrossPost?.platforms ?? [];
    $[6] = autoCrossPost?.platforms;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = new Set(t4);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const autoPlatforms = t5;
  const autoBoardCards = autoCrossPost?.boardCards ?? false;
  let t6;
  if ($[10] !== autoBoardCards || $[11] !== autoPlatforms || $[12] !== queryClient) {
    t6 = async platform_0 => {
      const [error, data] = await client.crosspost.updateAutoCrossPostPlatform.mutate({
        platform: platform_0,
        enabled: !autoPlatforms.has(platform_0)
      });
      if (error) {
        toast(error.message || "Failed to update auto cross-post");
        return;
      }
      queryClient.setQueryData(["autoCrossPostSettings"], {
        platforms: data?.platforms ?? [],
        boardCards: autoBoardCards
      });
    };
    $[10] = autoBoardCards;
    $[11] = autoPlatforms;
    $[12] = queryClient;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const toggleAuto = t6;
  let t7;
  if ($[14] !== autoBoardCards || $[15] !== autoPlatforms || $[16] !== queryClient) {
    t7 = async () => {
      const [error_0, data_0] = await client.crosspost.updateAutoCrossPostBoardCards.mutate({
        enabled: !autoBoardCards
      });
      if (error_0) {
        toast(error_0.message || "Failed to update board card cross-posting");
        return;
      }
      queryClient.setQueryData(["autoCrossPostSettings"], {
        platforms: [...autoPlatforms],
        boardCards: data_0?.boardCards ?? false
      });
    };
    $[14] = autoBoardCards;
    $[15] = autoPlatforms;
    $[16] = queryClient;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const toggleBoardCards = t7;
  let t8;
  if ($[18] !== autoPlatforms || $[19] !== hasIssue || $[20] !== toggleAuto) {
    t8 = platform_1 => ({
      autoEnabled: autoPlatforms.has(platform_1),
      issue: hasIssue(platform_1),
      onToggleAuto: () => toggleAuto(platform_1)
    });
    $[18] = autoPlatforms;
    $[19] = hasIssue;
    $[20] = toggleAuto;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const cardProps = t8;
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div><h2 className="text-2xl font-semibold">Connected Apps</h2><p className="text-textSecondary/60">Manage third-party apps and agents connected to your account.</p></div>;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== anyIssue) {
    t10 = anyIssue && <p className="flex items-center gap-1.5 text-xs text-textSecondary"><span className="size-1.5 rounded-full bg-amber-500" />Limited networks have an issue on our side. Posts there may fail until it's fixed.</p>;
    $[23] = anyIssue;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== cardProps) {
    t11 = <TiktokCard {...cardProps("TIKTOK")} />;
    $[25] = cardProps;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== cardProps) {
    t12 = <YoutubeCard {...cardProps("YOUTUBE")} />;
    $[27] = cardProps;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== cardProps) {
    t13 = <InstagramCard {...cardProps("INSTAGRAM")} />;
    $[29] = cardProps;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== cardProps) {
    t14 = <FacebookCard {...cardProps("FACEBOOK")} />;
    $[31] = cardProps;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== cardProps) {
    t15 = <TwitterCard {...cardProps("TWITTER")} />;
    $[33] = cardProps;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== cardProps) {
    t16 = <LinkedinCard {...cardProps("LINKEDIN")} />;
    $[35] = cardProps;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== toggleBoardCards) {
    t17 = () => void toggleBoardCards();
    $[37] = toggleBoardCards;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== autoBoardCards || $[40] !== t17) {
    t18 = <AutoCrossPostBoardCardsToggle enabled={autoBoardCards} onToggle={t17} />;
    $[39] = autoBoardCards;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t10 || $[43] !== t11 || $[44] !== t12 || $[45] !== t13 || $[46] !== t14 || $[47] !== t15 || $[48] !== t16 || $[49] !== t18) {
    t19 = <IntegrationSection title="Cross-posting" description="Publish your Factiii posts to other networks. Auto-post sends every new post without asking." notice={t10}>{t11}{t12}{t13}{t14}{t15}{t16}{t18}</IntegrationSection>;
    $[42] = t10;
    $[43] = t11;
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
    $[49] = t18;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <IntegrationSection title="Imports" description="Bring outside content into your spaces."><TrelloCard /></IntegrationSection>;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  let t22;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <IntegrationSection title="File Vault" description={"A cloud drive Factiii\u2019s servers can read, so the vault can import and search your documents."}><OneDriveServerCard /></IntegrationSection>;
    t22 = <RunnersList />;
    $[52] = t21;
    $[53] = t22;
  } else {
    t21 = $[52];
    t22 = $[53];
  }
  let t23;
  if ($[54] !== t19) {
    t23 = <div className="space-y-8 p-6">{t9}{t19}{t20}{t21}{t22}</div>;
    $[54] = t19;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  return t23;
};
export default ConnectedApps;
function _temp() {
  return unwrapFromResult(client.crosspost.getAutoCrossPostSettings.query());
}
function _temp2() {
  return unwrapFromResult(client.crosspost.getPlatformHealth.query());
}